import Vue from 'vue';
import Router from 'vue-router';
import toastr from 'toastr';
import routes from '@/routes';
import { i18n } from '@/plugins/i18n';
// Vuex utils
import { CHECK_LOGIN } from '@/store/action-types';
import { SETUP_LANGUAGE } from '@/store/modules/language/action-types';
// Utils
import { ROLE_ADMIN } from '@/utils/constants';
import { getBrowserLanguages, setLanguageRelatedDefaults } from '@/utils/language';
import { preventPastingStyles } from '@/utils/main';
import { globalEventDispatcher, ShizenGlobalEvent } from '@/utils/event-dispatcher/global';
import { confirmLeavingQueryParams } from '@/utils/navigation-constants';

Vue.use(Router);

export const router = new Router({
  mode: 'history',
  routes,
});

export function setRouterHooks(routerParam) {
  // Prevent creating many actions/info/problems when clicking repeatedly
  routerParam.beforeEach((to, from, next) => {
    let handledByModal = false;
    confirmLeavingQueryParams.forEach((toQParam) => {
      // If starting a new edition modal
      if (to.query[toQParam] !== undefined && Number(to.query[toQParam]) === 0) {
        confirmLeavingQueryParams.forEach((fromQParam) => {
          // If an edition modal was there already
          if (from.query[fromQParam] !== undefined && Number(from.query[fromQParam]) > 0) {
            // Creating a new action/info/problem while the modal is active.
            // Let the modal handle it, to prevent losing any ongoing change.
            // If the modal is in the process of creating a new action/info/problem,
            // the user will be prompted to keep/delete the new data or to cancel the navigation
            globalEventDispatcher.dispatch(ShizenGlobalEvent.NEW_MODAL_WHILE_ONE_ACTIVE, { next });
            handledByModal = true;
          }
        });
      }
    });
    if (!handledByModal) {
      next();
    }
  });

  // Setup language
  routerParam.beforeEach((to, from, next) => {
    if (routerParam.app.$store) {
      if (routerParam.app.$store.state.language.language) {
        setLanguageRelatedDefaults(routerParam.app.$store.state.language.language);
        next();
      } else {
        routerParam.app.$store.dispatch(`language/${SETUP_LANGUAGE}`, getBrowserLanguages())
          .then(() => {
            setLanguageRelatedDefaults(routerParam.app.$store.state.language.language);
            next();
          });
      }
    } else {
      next();
    }
  });

  routerParam.beforeEach((to, from, next) => {
    let preventNext = false;
    const app = routerParam.app.$children[0];
    // If it's a meeting page and user is not in a meeting, redirect to dashboard
    if (to.matched.some(record => record.meta.requireMeeting)
      && !routerParam.app.$store.state.meeting.isMeetingInProgress
    ) {
      globalEventDispatcher.dispatch(ShizenGlobalEvent.PUSH_HOME_PAGE);
      return;
    }

    // If it's a config page, check login
    if (to.matched.some(record => record.meta.requireConfig)) {
      next();
      routerParam.app.$store.dispatch(CHECK_LOGIN)
        .then((data) => {
          if (!data || !data.id || !data.roles.includes(ROLE_ADMIN)) {
            globalEventDispatcher.dispatch(ShizenGlobalEvent.PUSH_HOME_PAGE);
          }
        })
        .catch(() => {
          globalEventDispatcher.dispatch(ShizenGlobalEvent.PUSH_HOME_PAGE);
        });
    }

    // Free covid version, some pages aren't accessible
    if (to.matched.some(record => record.meta.requireNonCovid)) {
      const { currentTeam } = routerParam.app.$store.state;
      const restrictedTxt = i18n.t('message.FREE_VERSION_RESTRICTED') + i18n.t('message.GET_FULL_SHIZEN_LINK');
      if (currentTeam && currentTeam.covidVersion) {
        toastr.remove();
        toastr.info(restrictedTxt, '', { timeOut: 8000, preventDuplicates: false });
        preventNext = true;
      } else if (!currentTeam) {
        routerParam.app.$store.dispatch(CHECK_LOGIN)
          .then((data) => {
            if (data && data.id && data.covidVersion) {
              toastr.remove();
              toastr.info(restrictedTxt, '', { timeOut: 8000 });
              if (to.meta.fallbackRoute && to.meta.fallbackRoute !== '/') {
                routerParam.push(to.meta.fallbackRoute);
              } else {
                globalEventDispatcher.dispatch(ShizenGlobalEvent.PUSH_HOME_PAGE);
              }
            }
          });
      }
    }

    // GW page. Check access
    if (to.matched.some(record => record.meta.requireGW)
       && (!app || !app.currentTeam || Number(app.currentTeam.accessGW) !== 1)) {
      Vue.nextTick(() => {
        routerParam.app.$store.dispatch(CHECK_LOGIN).then((data) => {
          if (data == null || data === undefined || !data.accessGW || Number(data.accessGW) !== 1) {
            toastr.error(i18n.t('message.GW_RESTRICTED_ACCESS'));
            if (from.fullPath === '/') {
              globalEventDispatcher.dispatch(ShizenGlobalEvent.PUSH_HOME_PAGE);
            } else {
              routerParam.push(from.fullPath);
            }
          }
        });
      });
    }

    if (!preventNext) {
      next();
    }
  });

  routerParam.afterEach(() => {
    preventPastingStyles();
  });
}

setRouterHooks(router);

export default {
  router,
  setRouterHooks,
};
